import {
  Autocomplete,
  Badge,
  Box,
  Card,
  Checkbox,
  createFilterOptions,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import React, { useEffect, useRef, useState } from "react";
import AngleRightIcon from "common/customIcons/angle_right";
import MDButton from "components/MDButton";
import colors from "assets/theme/base/colors";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Zoom from "react-medium-image-zoom";
import defaultProductImage from "../../../assets/images/placeholderproduct.svg";
import { capitalizeText } from "utilities/common";
import { isEmptyObject } from "utilities/common";
import { getData } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import { useDebounce } from "hooks/useDebounce";
import EditIcon from "common/customIcons/edit";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from "../../../redux/slices/modalSlice";
import { openSnackbar } from "../../../redux/slices/common";
import DeleteIcon from "common/customIcons/delete";
import ActiveIcon from "common/customIcons/active";
import InActiveIcon from "common/customIcons/inactive";
import ProductTraits from "./productTraits";
import {
  deleteProductStrain,
  getAllHyperwolfStrains,
} from "../../../redux/slices/hyperwolf/strains";
import { addProductStrains, fetchSingleProduct } from "../../../redux/slices/hyperwolf/products";
import SeoContent from "./SeoContent";

// Trims option labels before matching so stray leading/trailing whitespace in
// strain names (seen in the API data) doesn't break the search-as-you-type filter.
const strainFilterOptions = createFilterOptions({
  stringify: (option) => (option?.strainName ?? "").toString().trim(),
  trim: true,
});

export default function ProductDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const { singleProduct = {}, loading } = useSelector((state) => state.hyperwolfProducts);
  const { productBatches = {} } = useSelector((state) => state.products);
  const [refreshLogs, setRefreshLogs] = useState(false);
  const { productDetails = {} } = singleProduct?.data || {};
  const [linkState, setLinkState] = useState("");
  const { totalStrain } = useSelector((state) => state.hyperwolfStrains);

  let localUser = getData("login-user-info");
  if (localUser) {
    try {
      localUser = JSON.parse(localUser);
    } catch {
      localUser = null;
    }
  }
  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
  const canEditProduct = hasSectionPermission(permissionsForMode, "Product", "Edit/Update");

  const {
    assets: detailsAssets,
    adminProductName: detailsProductName,
    websiteProductName,
    salePrice: detailsSalePrice,
    brandSlug: detailsBrandSlug,
    strainType: detailsStrainType,
    thcData,
    productDescription: detailsDescription,
    ingredientStatus,
    ingredients,
    instructionStatus,
    instructions,
    matchedStrains,
    productData = {},
    productSaleType: detailsProductSaleType,
  } = productDetails || {};

  const {
    unitPrice,
    thc,
    active,
    customWeight,
    weightPerUnit,
    customGramType,
    category,
    vendor,
    vendorName: dataVendorName,
    productSaleType: dataProductSaleType,
    description: dataDescription,
    name: dataName,
    flowerType,
    // cannabisType,
    tags: dataTags,
    salesPrice,
  } = productData || {};

  // Normalize images from productDetails.assets or productData.assets (objects with publicURL/origURL/largeURL or string URLs)
  const productImages = React.useMemo(() => {
    const list = detailsAssets ?? productData?.assets ?? [];
    return (Array.isArray(list) ? list : [])
      .map((a) => (typeof a === "string" ? a : a?.publicURL ?? a?.origURL ?? a?.largeURL ?? a?.mediumURL))
      .filter(Boolean);
  }, [detailsAssets, productData?.assets]);

  const adminProductName = detailsProductName ?? websiteProductName ?? dataName ?? "";
  const salePrice = detailsSalePrice ?? salesPrice;
  const brandSlug = detailsBrandSlug ?? productData?.brand?.name ?? "";
  const productDescription = detailsDescription ?? dataDescription ?? "";
  const productSaleType = detailsProductSaleType ?? dataProductSaleType ?? "";
  const vendorName = productData?.vendor?.name ?? dataVendorName ?? "";
  const productTags = dataTags ?? productDetails?.tags;
  const primaryStrainName = matchedStrains?.[0]?.strainName ?? flowerType ?? detailsStrainType ?? productDetails?.strainName ?? "";
  const productSku = productDetails?.sku ?? productData?.sku ?? "";
  const { grey } = colors;
  const formatValue = (value, emptyFallback = "-") => {
    if (value === null || value === undefined) return emptyFallback;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === "-" || normalized === "--" || normalized === "null" || normalized === "undefined") {
        return emptyFallback;
      }
      return value;
    }
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) {
      const items = value.map((v) => formatValue(v, "")).filter(Boolean);
      return items.length ? items.join(", ") : emptyFallback;
    }
    if (typeof value === "object") {
      const preferred =
        value?.name ||
        value?.title ||
        value?.label ||
        value?.slug ||
        value?.vendorName ||
        value?.brandName ||
        value?.strainName ||
        value?._id;
      if (preferred) return String(preferred);
      try {
        return JSON.stringify(value);
      } catch {
        return emptyFallback;
      }
    }
    return emptyFallback;
  };

  const safeHtml = (value) =>
    typeof value === "string" && value.trim() ? value : "<p>-</p>";

  // Strain records from the API can have inconsistent casing/whitespace in strainName
  // (e.g. "Pineapple Express "), so match/dedupe on the stable subStrainSlug when available.
  const getStrainKey = (item) =>
    (item?.subStrainSlug || item?.strainName || "").toString().trim().toLowerCase();
  // subStrainSlug is optional on catalog strain records (see AddStrain.jsx), so two distinct
  // records can legitimately share the same missing-slug + strainName combo. Use the
  // always-present, always-unique _id to identify catalog items amongst themselves so a
  // genuinely different record never gets silently dropped as a "duplicate" of another.
  const getCatalogItemId = (item) =>
    (item?._id || item?.subStrainSlug || item?.strainName || "").toString().trim().toLowerCase();

  const [isBatchAddOpen, setIsBatchAddOpen] = useState(false);
  const [editBatch, setEditBatch] = useState({});
  const [strainData, setStrainData] = useState([]);
  const STRAIN_PAGE_SIZE = 100;
  const strainSkipRef = useRef(0);
  const strainFetchingRef = useRef(false);
  const strainRequestIdRef = useRef(0);
  // hasMoreStrains/strainSearchTerm are also mirrored into refs: the scroll handler
  // is registered via ListboxProps and can end up holding an older render's closure,
  // so it must read the CURRENT value through a ref rather than a possibly-stale
  // state closure — otherwise a scroll event can fire a browse-mode "load more" using
  // a search term from before the user's last few keystrokes.
  const [hasMoreStrains, setHasMoreStrainsState] = useState(true);
  const hasMoreStrainsRef = useRef(true);
  const setHasMoreStrains = (value) => {
    hasMoreStrainsRef.current = value;
    setHasMoreStrainsState(value);
  };
  const [loadingMoreStrains, setLoadingMoreStrains] = useState(false);
  // Distinct from loadingMoreStrains: true only while a reset (search) fetch is in
  // flight, never during a plain page-2+ append, so the dropdown only hides stale
  // results for an actual search, not while scrolling loads more of the current page.
  const [isStrainSearchLoading, setIsStrainSearchLoading] = useState(false);
  const [strainInputValue, setStrainInputValue] = useState("");
  const [strainSearchTerm, setStrainSearchTermState] = useState("");
  const strainSearchTermRef = useRef("");
  const setStrainSearchTerm = (value) => {
    strainSearchTermRef.current = value;
    setStrainSearchTermState(value);
  };
  const debouncedStrainSearch = useDebounce((term) => {
    setStrainSearchTerm(term);
  }, 500);
  const [formData, setFormData] = useState({
    productId: "", // You might generate this automatically
    adminProductName: "",
    productImages: [],
    status: "inactive",
    isSalePrice: false,
    unitPrice: 0,
    salePrice: "",
    categoryName: "",
    brandSlug: "",
    // createdBy: user?.name || '',
    tags: [],
    strainSlug: "",
    strainType: "",
    infoEffects: [],
    categoryId: "",
    sku: "",
    brandDescription: "",
    productDescription: "",
    purchaseDate: undefined,
    uniqueNumber: "",
    expirationDate: null,
    trackingSystem: "",
    purchaseQuantity: "",
    currentQuantity: "",
    unitCost: "",
    batchStatus: "active",
    ingredientStatus: false,
    ingredients: "",
    instructionStatus: false,
    instructions: "",
    strainName: [], // Initialize as empty array
    subStrainSlug: [], // Initialize as empty array
    selectedStrains: [],
  });

  const showError = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const handleSave = () => {
    const strainArray = formData.strainName.map((strain, index) => ({
      strainName: strain,
      subStrainSlug: formData.subStrainSlug[index],
    }));

    dispatch(addProductStrains({ strainArray, productId }))
      .unwrap()
      .then((res) => {
        showError(res?.data?.message, "success");
        setFormData({
          ...formData,
          strainName: [], // Clear the strainName field after saving
          subStrainSlug: [], //
        });
        dispatch(fetchSingleProduct(productId)); // Refresh product data
      })
      .catch((err) => {
        showError(err?.message, "error");
      });
  };

  useEffect(() => {
    setTimeout(() => {
      setLinkState("");
    }, 1000);
  }, [linkState]);

  useEffect(() => {
    setRefreshLogs(true);
  }, [productBatches]);

  const queryParams = new URLSearchParams(location.search);
  const filter = queryParams.get("filter");
  useEffect(() => {
    if (filter === "activity_logs") {
      setIsBatchAddOpen(!isBatchAddOpen);
    }
  }, [filter]);

  useEffect(() => {
    if (!isEmptyObject(editBatch)) {
      const {
        purchaseDate,
        uniqueNumber,
        expirationDate,
        trackingSystem,
        purchaseQuantity,
        status,
        currentQuantity,
        unitCost,
      } = editBatch || {};
      setFormData({
        ...formData,
        ...{
          purchaseDate: dayjs(purchaseDate),
          uniqueNumber,
          expirationDate: expirationDate ? dayjs(expirationDate) : null,
          batchStatus: status,
          trackingSystem,
          purchaseQuantity,
          currentQuantity,
          unitCost,
        },
      });
    }
  }, [editBatch]);

  const handleAddNewStrain = () => {
    dispatch(
      openCustomModalDialog({
        modalData: { productId: productId, fetchProductStrainsData },
        modal: "ADD_NEW_STRAIN_MODAL",
      })
    );
  };

  const fetchProductStrainsData = () => {
    dispatch(fetchSingleProduct(productId)).unwrap();
  };

  const loadMoreStrains = async ({ reset = false } = {}) => {
    // A page-scroll fetch can be skipped if one's already running or there's nothing
    // more to load. A reset (new search term / matchedStrains change) must NEVER be
    // dropped this way — it always starts a fresh request; requestId below then makes
    // sure only the most recently-issued request is allowed to write to state, so an
    // older, slower response (e.g. for a since-abandoned search term) can't land after
    // and clobber the newer one.
    if (!reset && (strainFetchingRef.current || !hasMoreStrainsRef.current)) return;
    const requestId = strainRequestIdRef.current + 1;
    strainRequestIdRef.current = requestId;
    strainFetchingRef.current = true;
    setLoadingMoreStrains(true);
    // Only a reset (search) fetch should make the dropdown hide stale results — a plain
    // page-2+ append (scroll pagination) must keep showing the already-loaded page while
    // it fetches more, or the listbox collapses to just "Add new strain" mid-scroll.
    if (reset) setIsStrainSearchLoading(true);
    const skip = reset ? 0 : strainSkipRef.current;
    try {
      const fetchStrainResponse = await dispatch(
        getAllHyperwolfStrains({ skip, limit: STRAIN_PAGE_SIZE, searchTerm: strainSearchTermRef.current })
      ).unwrap();
      if (requestId !== strainRequestIdRef.current) return; // superseded by a newer request
      // Mirror the API data as-is; only drop strains already attached to this product.
      const strainDataArray = fetchStrainResponse.strain || [];
      const matchedKeys = new Set((matchedStrains || []).map(getStrainKey));
      const filteredPage = strainDataArray.filter((item) => !matchedKeys.has(getStrainKey(item)));
      setStrainData((prev) => {
        if (reset) return filteredPage;
        // Appending a page should never re-add a strain already in the list (e.g. if a
        // stray "load more" scroll event fires for a page that was already loaded).
        const existingKeys = new Set(prev.map(getCatalogItemId));
        const newItems = filteredPage.filter((item) => !existingKeys.has(getCatalogItemId(item)));
        return [...prev, ...newItems];
      });
      const nextSkip = skip + strainDataArray.length;
      strainSkipRef.current = nextSkip;
      setHasMoreStrains(nextSkip < (fetchStrainResponse.total || 0));
    } catch (error) {
      // leave hasMoreStrains as-is so the next scroll attempt can retry
    } finally {
      if (requestId === strainRequestIdRef.current) {
        strainFetchingRef.current = false;
        setLoadingMoreStrains(false);
        if (reset) setIsStrainSearchLoading(false);
      }
    }
  };

  useEffect(() => {
    // Re-fetch from the first page when the product's already-matched strains change
    // (e.g. after save/delete), or when the debounced search term changes — not on
    // every keystroke or in-progress selection.
    strainSkipRef.current = 0;
    setHasMoreStrains(true);
    loadMoreStrains({ reset: true });
  }, [matchedStrains, strainSearchTerm]);

  const handleStrainListboxScroll = (event) => {
    // Scroll-triggered pagination is for browsing the full unfiltered list only. While a
    // search is active, every reset legitimately replaces strainData with a small, fresh
    // result set; an append landing on top of that (e.g. because this handler's closure
    // is from a render before the last few keystrokes, so it still reads an older search
    // term) would silently mix a browse-mode page into the current search's results.
    if (strainSearchTermRef.current) return;
    const listboxNode = event.currentTarget;
    // A short list (e.g. a narrow search result) has nothing to scroll — without this
    // check the "near bottom" math is trivially true for it, and shrinking the list
    // (typing a search term) can make the browser auto-clamp scrollTop and fire a
    // native scroll event on its own, triggering an unwanted extra page fetch.
    const canScroll = listboxNode.scrollHeight > listboxNode.clientHeight;
    // Fire the next page fetch at 85% scrolled, not at the very bottom — gives the
    // request time to resolve before the user actually reaches the end of the list.
    const nearBottom =
      canScroll &&
      (listboxNode.scrollTop + listboxNode.clientHeight) / listboxNode.scrollHeight >= 0.85;
    if (nearBottom) {
      loadMoreStrains();
    }
  };

  // Stable references so typing in the strain search box (which re-renders this
  // component on every keystroke) doesn't hand the Autocomplete a brand-new
  // options/value array each time — that was resetting its internal input state
  // and made it impossible to type continuously.
  const strainOptions = React.useMemo(
    () => [{ strainName: "+ Add new strain" }, ...(strainData || [])],
    [strainData]
  );
  const selectedStrainValues = React.useMemo(
    () =>
      formData.strainName.map((name, index) => {
        const slug = formData.subStrainSlug[index];
        const key = (slug || name || "").toString().trim().toLowerCase();
        return (
          strainData.find((strain) => getStrainKey(strain) === key) || {
            strainName: name,
            subStrainSlug: slug,
          }
        );
      }),
    [formData.strainName, formData.subStrainSlug, strainData]
  );

  const deleteHandler = (deleteId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteProductStrain({ productId, subStrainSlug: deleteId }))
      .unwrap()
      .then((res) => {
        showError(res?.data?.message, "success");
        dispatch(closeCustomModalDialog());
        dispatch(fetchSingleProduct(productId)).unwrap();
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showError(err?.message, "error");
      });
  };

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }} className="static-pages-container">
      <Stack direction="row" justifyContent="space-between" spacing={3} mb={3}>
        <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
          <IconButton
            onClick={() => {
              const q = location.state?.productsListQuery;
              if (q != null && String(q).trim() !== "") {
                navigate(`/hyperwolf/products?${q}`);
                return;
              }
              const s = location.state?.productsListSearch;
              if (s != null && String(s).trim() !== "") {
                navigate(
                  `/hyperwolf/products?search=${encodeURIComponent(String(s).trim())}`
                );
                return;
              }
              navigate("/hyperwolf/products");
            }}
          >
            <AngleRightIcon fontSize="14px" />
          </IconButton>
          <Typography variant="h6" fontWeight="semibold" ml={0.75}>
            Products
          </Typography>
        </Box>

      </Stack>
      {
        <>
          <Card sx={{ p: 3, mb: 4, borderRadius: 2.5, boxShadow: "none" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              flexWrap="wrap"
              rowGap={1.5}
              mb={3}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Product Information
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="flex-end"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.75}
                  lineHeight={1}
                  p={0.75}
                  borderRadius={2}
                >
                  {active && (
                    <Box color="#24CA49" display="flex" alignItems="center">
                      <ActiveIcon width="18px" height="18px" flex="none" />
                      <Typography
                        variant="caption"
                        fontWeight="regular"
                        ml={0.5}
                        textTransform="capitalize"
                      >
                        Active
                      </Typography>
                    </Box>
                  )}
                  {!active && (
                    <Box color="#FD4438" display="flex" alignItems="center">
                      <InActiveIcon width="18px" height="18px" flex="none" />
                      <Typography
                        variant="caption"
                        fontWeight="regular"
                        ml={0.5}
                        textTransform="capitalize"
                      >
                        Inactive
                      </Typography>
                    </Box>
                  )}
                </Stack>
                {canEditProduct && productId ? (
                  <MDButton
                    type="button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon width="15px" height="15px" />}
                    onClick={() =>
                      navigate(`/hyperwolf/products/edit/${productId}`, {
                        state: location.state,
                      })
                    }
                  >
                    Edit
                  </MDButton>
                ) : null}
              </Box>
            </Stack>

            {/* Product Images */}
            <Typography variant="body2" fontWeight="medium" color={grey[700]} sx={{ mb: 1.5 }}>
              Product Images
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
              {productImages?.length > 0 ? (
                productImages?.map((productImageUrl) => (
                  <Zoom key={productImageUrl} classDialog="custom-zoom">
                    <img
                      style={{
                        borderRadius: "12px",
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                      }}
                      src={productImageUrl}
                      alt="Product"
                    />
                  </Zoom>
                ))
              ) : (
                <img
                  style={{
                    borderRadius: "12px",
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                  src={defaultProductImage}
                  alt="No Image"
                />
              )}
            </Stack>

            {/* Product details – two-column label/value grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>
                  Product Name
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={0.75}>
                  <Typography variant="body1" fontWeight="500">
                    {adminProductName ? capitalizeText(formatValue(adminProductName)) : "-"}
                  </Typography>
                  <svg
                    style={{ width: "18px", height: "18px", cursor: "pointer", flexShrink: 0 }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="21"
                    viewBox="0 0 21 21"
                    fill="none"
                    onClick={() => {
                      if (adminProductName) {
                        navigator.clipboard
                          .writeText(formatValue(adminProductName))
                          .then(() => setLinkState("Link copied!"))
                          .catch(() => { });
                      }
                    }}
                  >
                    <g clipPath="url(#clip0_14557_35992)">
                      <path
                        d="M8.61203 16.7513C6.65952 16.7513 5.07031 15.162 5.07031 13.2095V4.66797H3.61203C2.34784 4.66797 1.32031 5.69534 1.32031 6.95953V18.2095C1.32031 19.4737 2.34784 20.5013 3.61203 20.5013H14.0286C15.2928 20.5013 16.3203 19.4737 16.3203 18.2095V16.7513H8.61203Z"
                        fill="currentColor"
                      />
                      <path
                        d="M19.6536 2.79172C19.6536 1.52585 18.6277 0.5 17.362 0.5H8.61203C7.34616 0.5 6.32031 1.52585 6.32031 2.79172V13.2083C6.32031 14.4742 7.34616 15.5 8.61203 15.5H17.362C18.6277 15.5 19.6536 14.4742 19.6536 13.2083V2.79172Z"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_14557_35992">
                        <rect width="20" height="20" fill="currentColor" transform="translate(0.5 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                  {linkState && <Typography variant="caption" color="text.secondary">{linkState}</Typography>}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Category</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(category?.name || category)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Brand</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(brandSlug)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Distributor</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(vendorName)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Weight Per Unit (WPU)</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(weightPerUnit)}</Typography>
              </Grid>
              {/* {customWeight != null && customWeight !== "" && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color={grey[600]} gutterBottom>Custom Weight</Typography>
                  <Typography variant="body1" fontWeight="500">{customWeight}</Typography>
                </Grid>
              )}
              {customGramType ? (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color={grey[600]} gutterBottom>Custom Gram Type</Typography>
                  <Typography variant="body1" fontWeight="500">{customGramType}</Typography>
                </Grid>
              ) : null} */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Retail Price</Typography>
                <Typography variant="body1" fontWeight="500">${unitPrice ?? 0}</Typography>
              </Grid>

              {/* <Typography variant="body2" color={grey[600]} gutterBottom>Sale Price</Typography> */}


              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Sell Type</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(productSaleType)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>SKU</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(productSku)}</Typography>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Cannabis Type</Typography>
                <Typography variant="body1" fontWeight="500">{cannabisType || "-"}</Typography>
              </Grid> */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Strain</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(primaryStrainName)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>THC ({thcData?.includes("%") ? "%" : "mg"})</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(thc)}</Typography>
              </Grid>

            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ mb: 3 }}>
              <Typography variant="body2" color={grey[600]} gutterBottom>Tags</Typography>
              <Typography variant="body1" fontWeight="500">
                {Array.isArray(productTags) && productTags.length > 0
                  ? productTags.map((tag) => formatValue(tag, "")).filter(Boolean).join(", ")
                  : "-"}
              </Typography>
            </Grid>

            {/* Description */}
            <Typography variant="body2" fontWeight="medium" color={grey[600]} sx={{ mb: 1 }}>
              Description
            </Typography>
            <Box sx={{ pb: 2 }}>
              <Typography component="div" variant="body2" fontWeight="medium">
                {productDescription ? (
                  <div dangerouslySetInnerHTML={{ __html: safeHtml(productDescription) }} />
                ) : (
                  "-"
                )}
              </Typography>
            </Box>

            {ingredientStatus && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Ingredients</Typography>
                <Box className="quill-text-editor-view" sx={{ p: 2, borderRadius: 1, bgcolor: grey[100] }}>
                  <Typography component="div" variant="body2" fontWeight="medium">
                    {ingredients ? <div dangerouslySetInnerHTML={{ __html: safeHtml(ingredients) }} /> : "-"}
                  </Typography>
                </Box>
              </Box>
            )}
            {instructionStatus && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Instructions for use</Typography>
                <Box className="quill-text-editor-view" sx={{ p: 2, borderRadius: 1, bgcolor: grey[100] }}>
                  <Typography component="div" variant="body2" fontWeight="medium">
                    {instructions ? <div dangerouslySetInnerHTML={{ __html: safeHtml(instructions) }} /> : "-"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid item xs={12} className="mb-2">
                <Typography variant="body2" color={grey[600]}>
                  Genetics (Strains)
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className="bg-grey m-0 genetics-strains-sec"
              >
                <Grid container spacing={2} xs={12} md={12}>
                  <Grid item={12}>
                    {matchedStrains?.map((strain, index) => (
                      <Badge key={index}>
                        <div className="badge-inner">
                          {strain.strainName}
                          <IconButton
                            onClick={() =>
                              dispatch(
                                openCustomModalDialog({
                                  modalData: {
                                    deleteHandler,
                                    deleteId: strain.subStrainSlug,
                                    deleteType: "product strain",
                                  },
                                  modal: "DELETE_MODAL",
                                })
                              )
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </Badge>
                    ))}
                  </Grid>
                </Grid>
                <Grid container xs={12} md={12} className="mt-3 align-center select-multi-strain">
                  <Grid item xs={12}>
                    <div className="select-strain-sec">
                      <Autocomplete
                        multiple
                        id="strain-autocomplete"
                        options={strainOptions}
                        getOptionLabel={(option) => (option?.strainName ?? "").toString().trim()}
                        isOptionEqualToValue={(option, value) => getStrainKey(option) === getStrainKey(value)}
                        filterOptions={(options, state) => {
                          const addNewOption = options.find(
                            (opt) => opt.strainName === "+ Add new strain"
                          );
                          // strainData can still hold the previous search's results (or the
                          // initial unsearched browse page) for as long as 500ms after a
                          // keystroke — the debounce hasn't committed strainSearchTerm yet, or
                          // it has but the fetch it triggered hasn't landed. Client-filtering
                          // that stale data against the just-typed text can surface matches
                          // that don't belong to the current search at all (e.g. leftover
                          // browse-mode entries), so show nothing but "Add new strain" until
                          // strainData is confirmed to correspond to what's currently typed.
                          const isSearchPending =
                            strainInputValue.trim().toLowerCase() !== strainSearchTerm.trim().toLowerCase() ||
                            isStrainSearchLoading;
                          if (isSearchPending) {
                            return addNewOption ? [addNewOption] : [];
                          }
                          const rest = options.filter(
                            (opt) => opt.strainName !== "+ Add new strain"
                          );
                          const filtered = strainFilterOptions(rest, state);
                          return addNewOption ? [addNewOption, ...filtered] : filtered;
                        }}
                        filterSelectedOptions
                        ListboxProps={{ onScroll: handleStrainListboxScroll }}
                        loading={loadingMoreStrains}
                        inputValue={strainInputValue}
                        onInputChange={(event, newInputValue, reason) => {
                          // Only a real keystroke or the (x) clear button should touch the
                          // typed text or trigger a new search. MUI also fires this with
                          // reason "reset" and an empty value whenever the selected `value`
                          // array changes identity (e.g. right after a search's results
                          // land) — reacting to that blanked the input and re-triggered an
                          // empty search right after every real one.
                          if (reason === "input" || reason === "clear") {
                            setStrainInputValue(newInputValue);
                            debouncedStrainSearch(newInputValue);
                          }
                        }}
                        onChange={(event, selectedOptions) => {
                          const hasAddNew = selectedOptions.some(
                            (opt) => opt.strainName === "+ Add new strain"
                          );
                          // Selecting a strain clears the typed text (MUI resets it for
                          // multiple-select); reset the search term to match so the dropdown
                          // goes back to showing the full, unfiltered list.
                          setStrainInputValue("");
                          setStrainSearchTerm("");
                          if (hasAddNew) {
                            handleAddNewStrain();
                            // Remove "Add new strain" from selected options to prevent it from being displayed
                            const filteredOptions = selectedOptions.filter(
                              (opt) => opt.strainName !== "+ Add new strain"
                            );
                            setFormData((prev) => ({
                              ...prev,
                              strainName: filteredOptions.map((opt) => opt.strainName.trim()),
                              subStrainSlug: filteredOptions.map((opt) => opt.subStrainSlug),
                            }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              strainName: selectedOptions.map((opt) => opt.strainName.trim()),
                              subStrainSlug: selectedOptions.map((opt) => opt.subStrainSlug),
                            }));
                          }
                        }}
                        value={selectedStrainValues}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              formData.strainName.length > 0
                                ? `Select Strain (${formData.strainName.length} selected/${totalStrain?.total})`
                                : "Select Strain"
                            }
                          />
                        )}
                        disableCloseOnSelect
                        renderOption={(props, option, { selected }) => (
                          <li
                            {...props}
                            style={{
                              color: option.strainName === "+ Add new strain" ? "#0163FF" : "inherit", // Apply blue color to "Add new strain"
                            }}
                          >
                            {/* {option.strainName !== "+ Add new strain" && (
                            <Checkbox
                              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              checked={selected}
                              style={{ marginRight: 8 }}
                            />
                          )} */}
                            {option.strainName}
                          </li>
                        )}
                      />
                    </div>
                  </Grid>

                  {/* Edit Icon Button */}
                  {formData.strainName.length > 0 && (
                    <MDButton
                      onClick={handleSave}
                      size="small"
                      className="mr-3 primay-btn"
                      color="primary"
                      loading={loading}
                      disabled={loading}
                    >
                      Save
                    </MDButton>
                  )}
                </Grid>
              </Grid>
            </Grid>


          </Card>
        </>
      }

      <ProductTraits />
      {/* meta properties card */}
      <SeoContent productDetails={productDetails} />

    </Box>
  );
}
